import { useCallback } from 'react'
import { useSetRecoilState } from 'recoil'
import { useTranslation } from 'react-i18next'
import { useSetToast } from 'hooks'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { editorPageSetTurnedOverPromptAtom } from '@/atoms/_editorAtoms'
import { layoutSaveFailure } from '@/utils/editor/layoutSaveFailure'

/**
 * What the layout drawers show when a layout save fails. Every answer the
 * save routes give means nothing was written: a tab that loaded its pages before a
 * turnover closes the drawer and gets the editor's reload prompt, and every
 * other refusal says why. A request that got no answer, or a gateway's 502,
 * 503 or 504, has an unknown outcome, so it shows the error and asks the
 * caller to refresh its list.
 * Answers whether the outcome is unknown.
 */
export const useEditorLayoutSaveRefusal = (
  componentName: string,
  closeDrawer: (show: boolean) => void,
) => {
  const { t } = useTranslation()
  const setToast = useSetToast()
  const handleError = useErrorHandler(componentName)
  const setTurnedOverPrompt = useSetRecoilState(
    editorPageSetTurnedOverPromptAtom,
  )
  return useCallback(
    (error: unknown): boolean => {
      const failure = layoutSaveFailure(error)
      if (failure === 'turnedOver') {
        // The tab must reload either way; the prompt shows over the canvas,
        // not under the drawer.
        closeDrawer(false)
        setTurnedOverPrompt(true)
      } else if (failure === 'pagesGone')
        setToast({ text: t('toast.editorPagesReplaced') })
      else if (failure === 'lotusRefused')
        setToast({ text: t('toast.layoutGlobalTemplateLotusRefused') })
      else if (failure === 'noFuneralHome')
        setToast({ text: t('toast.layoutSaverHasNoFuneralHome') })
      else if (failure === 'notSaved')
        handleError(t('toast.layoutNotSavedNothingChanged'), error as Error)
      else handleError((error as Error)?.message, error as Error)
      return failure === 'unknown'
    },
    [closeDrawer, handleError, setToast, setTurnedOverPrompt, t],
  )
}
